import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useTranslation } from "react-i18next";
import {
  downloadSpaCommissionReport,
  getSpaCommissionReport,
  getSpaOperatorActivity,
  spaDisplayName,
} from "../APIs/SpaAPI";
import { useSnackbar } from "../contexts/SnackBarContext";

const inputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const initialFilters = () => {
  const today = new Date();
  return {
    operatorId: "",
    from: inputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: inputDate(today),
  };
};

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const clientName = (client) => spaDisplayName(client, "-");

function SpaOperatorCompensationReport({ token, tenant, operators = [] }) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const ct = useCallback((key, defaultValue, options = {}) => (
    t(`spa.finance.commissions.${key}`, { defaultValue, ...options })
  ), [t]);
  const [filters, setFilters] = useState(initialFilters);
  const [report, setReport] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const operatorOptions = useMemo(() => operators.map((operator) => ({
    id: Number(operator.id),
    label: spaDisplayName(operator, "Operator"),
  })).filter((operator) => Number.isInteger(operator.id)), [operators]);

  const invalidRange = !filters.from || !filters.to || filters.from > filters.to;
  const requestFilters = {
    from: filters.from,
    to: filters.to,
    ...(filters.operatorId ? { operatorId: Number(filters.operatorId) } : {}),
  };

  const changeFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
    setReport(null);
    setActivity(null);
    setError("");
  };

  const generateReport = async () => {
    if (invalidRange || !token || !tenant) return;
    setLoading(true);
    setError("");
    try {
      const [reportData, activityData] = await Promise.all([
        getSpaCommissionReport(requestFilters, token, tenant),
        filters.operatorId
          ? getSpaOperatorActivity(Number(filters.operatorId), {
            from: filters.from,
            to: filters.to,
          }, token, tenant)
          : Promise.resolve(null),
      ]);
      setReport(reportData);
      setActivity(activityData);
    } catch (loadError) {
      setReport(null);
      setActivity(null);
      setError(loadError?.message || ct("messages.reportError", "Unable to generate the operator report."));
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (invalidRange || !token || !tenant) return;
    setDownloading(true);
    setError("");
    try {
      const { blob, filename } = await downloadSpaCommissionReport(requestFilters, token, tenant);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      showSnackbar(ct("messages.reportDownloaded", "Compensation report downloaded."), "success");
    } catch (downloadError) {
      const message = downloadError?.message || ct("messages.reportDownloadError", "Unable to download the compensation report.");
      setError(message);
      showSnackbar(message, "error");
    } finally {
      setDownloading(false);
    }
  };

  const retailRows = (activity?.retailSales || []).flatMap((sale) => (
    (sale.products || []).map((product) => ({
      ...product,
      orderId: sale.orderId,
      soldAt: sale.soldAt,
      client: sale.client,
    }))
  ));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h6" fontWeight={850}>{ct("reportTitle", "Operator activity and compensation report")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {ct("reportHelp", "Review treatments, retail sales, revenue and compensation for the selected period.")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AssessmentOutlinedIcon />}
              disabled={loading || downloading || invalidRange} onClick={generateReport}>
              {ct("actions.generateReport", "Generate report")}
            </Button>
            <Button variant="outlined" startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={loading || downloading || invalidRange} onClick={downloadReport}>
              {ct("actions.downloadReport", "Download CSV")}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="compensation-report-operator-label">{ct("fields.operator", "Operator")}</InputLabel>
              <Select labelId="compensation-report-operator-label" label={ct("fields.operator", "Operator")}
                value={filters.operatorId} onChange={(event) => changeFilter("operatorId", event.target.value)}>
                <MenuItem value="">{ct("allOperators", "All operators")}</MenuItem>
                {operatorOptions.map((operator) => <MenuItem key={operator.id} value={String(operator.id)}>{operator.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }}
              label={ct("fields.from", "From")} value={filters.from}
              onChange={(event) => changeFilter("from", event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }}
              label={ct("fields.to", "To")} value={filters.to}
              onChange={(event) => changeFilter("to", event.target.value)} />
          </Grid>
        </Grid>

        {invalidRange ? <Alert severity="warning">{ct("validation.reportDates", "Select a valid date range.")}</Alert> : null}
        {!filters.operatorId ? <Alert severity="info">{ct("selectOperatorForActivity", "Select an operator to include treatment and retail activity details.")}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        {report ? (
          <Stack spacing={2}>
            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
              <Typography fontWeight={850} mb={1}>{ct("reportTotals", "Compensation totals")}</Typography>
              <Grid container spacing={1.5}>
                {[
                  ["entryCount", ct("metrics.entries", "Entries"), report.totals?.entryCount || 0],
                  ["treatmentBase", ct("metrics.treatmentRevenue", "Treatment revenue"), money(report.totals?.treatmentBase)],
                  ["retailBase", ct("metrics.retailRevenue", "Retail revenue"), money(report.totals?.retailBase)],
                  ["treatmentCompensation", ct("metrics.treatmentCompensation", "Treatment compensation"), money(report.totals?.treatmentCompensation)],
                  ["retailCompensation", ct("metrics.retailCompensation", "Retail compensation"), money(report.totals?.retailCompensation)],
                  ["totalCompensation", ct("metrics.totalCompensation", "Total compensation"), money(report.totals?.totalCompensation)],
                ].map(([key, label, value]) => (
                  <Grid item xs={6} md={2} key={key}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography fontWeight={850}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <TableContainer>
              <Table size="small" aria-label={ct("reportRows", "Compensation report entries")}>
                <TableHead><TableRow>
                  <TableCell>{ct("columns.date", "Date")}</TableCell>
                  <TableCell>{ct("fields.operator", "Operator")}</TableCell>
                  <TableCell>{ct("columns.client", "Client")}</TableCell>
                  <TableCell>{ct("columns.item", "Treatment / product")}</TableCell>
                  <TableCell>{ct("fields.source", "Source")}</TableCell>
                  <TableCell align="right">{ct("columns.base", "Base")}</TableCell>
                  <TableCell align="right">{ct("columns.commission", "Commission")}</TableCell>
                  <TableCell>{ct("columns.rule", "Rule")}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {!report.rows?.length ? <TableRow><TableCell colSpan={8}>{ct("emptyReport", "No compensation data for this period.")}</TableCell></TableRow> : null}
                  {(report.rows || []).map((row) => <TableRow key={row.entryId} hover>
                    <TableCell>{dateLabel(row.occurredAt)}</TableCell>
                    <TableCell>{spaDisplayName(row.operator, "Operator")}</TableCell>
                    <TableCell>{clientName(row.client)}</TableCell>
                    <TableCell>{row.item || "-"}</TableCell>
                    <TableCell>{ct(`source.${row.sourceType}`, row.sourceType)}</TableCell>
                    <TableCell align="right">{money(row.baseAmount)}</TableCell>
                    <TableCell align="right">{money(row.compensation)}</TableCell>
                    <TableCell>{row.rule?.name || row.rule?.code || "-"}</TableCell>
                  </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        ) : null}

        {activity ? (
          <Stack spacing={2}>
            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
              <Typography fontWeight={850}>{ct("activityTitle", "Operator activity")}: {spaDisplayName(activity.operator, "Operator")}</Typography>
              <Grid container spacing={1.5} mt={0}>
                {[
                  ["treatmentCount", ct("metrics.treatments", "Treatments"), activity.summary?.treatmentCount || 0],
                  ["retailSaleCount", ct("metrics.retailSales", "Retail sales"), activity.summary?.retailSaleCount || 0],
                  ["treatmentRevenue", ct("metrics.treatmentRevenue", "Treatment revenue"), money(activity.summary?.treatmentRevenue)],
                  ["retailRevenue", ct("metrics.retailRevenue", "Retail revenue"), money(activity.summary?.retailRevenue)],
                  ["totalRevenue", ct("metrics.totalRevenue", "Total revenue"), money(activity.summary?.totalRevenue)],
                ].map(([key, label, value]) => (
                  <Grid item xs={6} md={2} key={key}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography fontWeight={850}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography fontWeight={800} mb={1}>{ct("treatmentsTitle", "Treatments performed")}</Typography>
              <TableContainer>
                <Table size="small" aria-label={ct("treatmentsTitle", "Treatments performed")}>
                  <TableHead><TableRow>
                    <TableCell>{ct("columns.dateTime", "Date and time")}</TableCell>
                    <TableCell>{ct("columns.client", "Client")}</TableCell>
                    <TableCell>{ct("fields.treatment", "Treatment")}</TableCell>
                    <TableCell align="right">{ct("columns.price", "Price")}</TableCell>
                    <TableCell align="right">{ct("columns.discount", "Discount")}</TableCell>
                    <TableCell align="right">{ct("columns.revenue", "Revenue")}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {!activity.treatments?.length ? <TableRow><TableCell colSpan={6}>{ct("emptyTreatments", "No completed treatments for this period.")}</TableCell></TableRow> : null}
                    {(activity.treatments || []).map((item) => <TableRow key={item.appointmentId} hover>
                      <TableCell>{dateLabel(item.startedAt || item.completedAt)}</TableCell>
                      <TableCell>{clientName(item.client)}</TableCell>
                      <TableCell>{item.treatment?.name || "-"}</TableCell>
                      <TableCell align="right">{money(item.price)}</TableCell>
                      <TableCell align="right">{money(item.discount)}</TableCell>
                      <TableCell align="right">{money(item.revenue)}</TableCell>
                    </TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography fontWeight={800} mb={1}>{ct("retailTitle", "Products sold")}</Typography>
              <TableContainer>
                <Table size="small" aria-label={ct("retailTitle", "Products sold")}>
                  <TableHead><TableRow>
                    <TableCell>{ct("columns.dateTime", "Date and time")}</TableCell>
                    <TableCell>{ct("columns.client", "Client")}</TableCell>
                    <TableCell>{ct("fields.product", "Product")}</TableCell>
                    <TableCell align="right">{ct("columns.quantity", "Quantity")}</TableCell>
                    <TableCell align="right">{ct("columns.price", "Price")}</TableCell>
                    <TableCell align="right">{ct("columns.discount", "Discount")}</TableCell>
                    <TableCell align="right">{ct("columns.revenue", "Revenue")}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {!retailRows.length ? <TableRow><TableCell colSpan={7}>{ct("emptyRetail", "No retail products sold for this period.")}</TableCell></TableRow> : null}
                    {retailRows.map((item) => <TableRow key={`${item.orderId}-${item.orderProductId}`} hover>
                      <TableCell>{dateLabel(item.soldAt)}</TableCell>
                      <TableCell>{clientName(item.client)}</TableCell>
                      <TableCell>{item.product?.name || "-"}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{money(item.price)}</TableCell>
                      <TableCell align="right">{money(item.discount)}</TableCell>
                      <TableCell align="right">{money(item.revenue)}</TableCell>
                    </TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default SpaOperatorCompensationReport;
